// Mobile-first Linktree-style landing — vertical 9:16 mobile layout.
// Hidden: not linked from menu. Admin-controlled via /admin → Links.
import { useEffect, useState } from "react";
import { api, BACKEND_URL, formatApiError } from "../lib/api";
import {
  Leaf, Link as LinkIcon, FileDown, PlayCircle, MessageCircle, Mail, Instagram, Youtube, X, Loader2,
} from "lucide-react";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.31A8.16 8.16 0 0 0 22 9.66V6.21a4.85 4.85 0 0 1-2.41-.48v.96Z" />
  </svg>
);

const ICONS = {
  link: <LinkIcon size={18} />,
  url: <LinkIcon size={18} />,
  pdf: <FileDown size={18} />,
  video: <PlayCircle size={18} />,
  whatsapp: <MessageCircle size={18} />,
  email: <Mail size={18} />,
};

function resolveAssetUrl(value) {
  if (!value) return "";
  // New format: stored as "/api/uploads/..." — works through ingress.
  if (value.startsWith("/api/uploads/")) return `${BACKEND_URL}${value}`;
  // Legacy: older buttons stored as "/uploads/..." — rewrite for backward-compat.
  if (value.startsWith("/uploads/")) return `${BACKEND_URL}/api${value}`;
  return value;
}

function buildActionHref(type, value) {
  const v = resolveAssetUrl(value);
  if (type === "whatsapp") {
    const digits = v.replace(/[^0-9]/g, "");
    return digits ? `https://wa.me/${digits}` : v;
  }
  if (type === "email") {
    return v.startsWith("mailto:") ? v : `mailto:${v}`;
  }
  return v;
}

export default function Links() {
  const [cfg, setCfg] = useState(null);
  const [activeBtn, setActiveBtn] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/links").then(({ data }) => setCfg(data)).catch(() => setCfg({ buttons: [] }));
  }, []);

  const performAction = (type, value) => {
    if (!value) return;
    if (type === "pdf") {
      const url = resolveAssetUrl(value);
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
      a.download = "";
      document.body.appendChild(a); a.click(); a.remove();
      return;
    }
    const href = buildActionHref(type, value);
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const handleClick = async (btn) => {
    if (!btn.require_email) {
      performAction(btn.type, btn.value);
      return;
    }
    setActiveBtn(btn);
    setEmail("");
    setErr("");
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!activeBtn) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/links/leads", { email, button_id: activeBtn.id });
      setActiveBtn(null);
      performAction(data.type, data.value);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) {
    return (
      <div className="min-h-screen grid place-items-center bg-bone">
        <Loader2 className="w-8 h-8 animate-spin text-moss" />
      </div>
    );
  }

  const initials = (cfg.display_name || "D").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-bone" data-testid="links-page">
      <div className="relative mx-auto max-w-md sm:max-w-lg px-5 pt-10 pb-16">
        {/* soft blobs */}
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-moss/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-terra/10 blur-3xl pointer-events-none" />

        {/* Header */}
        <div className="relative flex flex-col items-center text-center" data-testid="links-header">
          {cfg.profile_image_url ? (
            <img
              src={resolveAssetUrl(cfg.profile_image_url)}
              alt={cfg.display_name}
              className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-hover"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-moss text-white grid place-items-center text-2xl font-heading font-semibold border-4 border-white shadow-hover">
              <span aria-hidden>{initials}</span>
              <span className="sr-only">{cfg.display_name}</span>
            </div>
          )}
          <h1 className="mt-5 font-heading text-2xl font-semibold text-ink" data-testid="links-name">
            {cfg.display_name}
          </h1>
          {cfg.tagline && (
            <p className="mt-1 text-sm font-semibold uppercase tracking-[0.15em] text-terra">{cfg.tagline}</p>
          )}
          {cfg.bio && (
            <p className="mt-3 text-sm text-muted2 leading-relaxed max-w-xs">{cfg.bio}</p>
          )}

          {(cfg.social_instagram || cfg.social_tiktok || cfg.social_youtube) && (
            <div className="mt-5 flex items-center gap-2" data-testid="links-socials">
              {cfg.social_instagram && (
                <a href={cfg.social_instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className="w-10 h-10 rounded-full bg-white border border-subtle grid place-items-center text-ink hover:text-moss hover:border-moss transition-all">
                  <Instagram size={18} />
                </a>
              )}
              {cfg.social_tiktok && (
                <a href={cfg.social_tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                  className="w-10 h-10 rounded-full bg-white border border-subtle grid place-items-center text-ink hover:text-moss hover:border-moss transition-all">
                  <TikTokIcon />
                </a>
              )}
              {cfg.social_youtube && (
                <a href={cfg.social_youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
                  className="w-10 h-10 rounded-full bg-white border border-subtle grid place-items-center text-ink hover:text-moss hover:border-moss transition-all">
                  <Youtube size={18} />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="relative mt-10 space-y-3" data-testid="links-buttons">
          {cfg.buttons.length === 0 ? (
            <div className="text-center text-muted2 text-sm bg-white border border-subtle rounded-2xl p-6">
              No links yet. Admin can add them in the dashboard.
            </div>
          ) : (
            cfg.buttons.map((b) => (
              <button
                key={b.id}
                onClick={() => handleClick(b)}
                data-testid={`link-button-${b.id}`}
                className="w-full inline-flex items-center justify-center gap-3 bg-white hover:bg-moss hover:text-white text-ink border-2 border-moss/30 hover:border-moss rounded-full px-6 py-4 font-semibold transition-all active:scale-[0.98] shadow-ambient hover:shadow-hover"
              >
                <span className="opacity-80">{ICONS[b.icon || b.type] || ICONS.link}</span>
                <span className="text-sm">{b.label}</span>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <a href="/" className="inline-flex items-center gap-1.5 text-xs text-soft hover:text-moss transition-colors">
            <Leaf size={12} /> Danashakaa.com
          </a>
        </div>
      </div>

      {/* Email capture modal */}
      {activeBtn && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => setActiveBtn(null)} data-testid="email-modal">
          <form onSubmit={submitEmail} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-hover relative">
            <button type="button" onClick={() => setActiveBtn(null)} aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full text-muted2 hover:bg-cardm">
              <X size={16} />
            </button>
            <h3 className="font-heading text-lg font-semibold text-ink">One quick step</h3>
            <p className="mt-1 text-sm text-muted2">
              Enter your email to {activeBtn.type === "pdf" ? "download" : "access"} <strong>{activeBtn.label}</strong>.
            </p>
            <input
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" autoFocus
              data-testid="email-capture-input"
              className="mt-4 w-full rounded-xl border border-subtle bg-white px-4 py-3 text-ink focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20"
            />
            {err && <p className="mt-2 text-sm text-danger">{err}</p>}
            <button type="submit" disabled={busy} data-testid="email-capture-submit"
              className="mt-4 w-full inline-flex justify-center items-center gap-2 bg-terra hover:bg-terra-dark text-white rounded-full px-5 py-3 font-semibold transition-all active:scale-95 disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} Continue
            </button>
            <p className="mt-3 text-xs text-soft text-center">We respect your inbox. No spam.</p>
          </form>
        </div>
      )}
    </div>
  );
}
